import { Stack } from '@understory-io/leaf/stack';
import { Text } from '@understory-io/leaf/text';
import { FormattedMessage, useIntl } from 'react-intl';
import { cx } from '~/styled-system/css';
import { css } from '@understory-io/leaf/css';
import { eventCard } from '~/styled-system/recipes';
import { EventLanguages, formatLanguageNames } from './stacked-avatars';
import { type EventCardSession } from './multi-session';
import {
  AvailabilityLabel,
  isSeatCountSuppressed,
  type AvailabilityStatus,
  type SeatCounterVisibility,
} from '~/ui/availability-label/availability-label';
import {
  formatLocation,
  formatTime,
  FormattedLocation,
  parseTime,
} from '../formatted';
import {
  EventTime,
  daySpan,
  deriveEventTimeKind,
  weekSpan,
} from '~/ui/event-time';
import { SessionDetailsDisclosure } from '~/ui/session-details-disclosure';

type EventCardProps = {
  /** Sessions on the event in start-time order. `length === 1` renders the
   *  single-session card; `length >= 2` renders the multi-session card with
   *  the expand-for-details affordance. */
  sessions: EventCardSession[];
  locales: string[];
  status: AvailabilityStatus;
  availableSeats: number;
  selected: boolean;
  onClick: () => void;
  seatCounterVisibility?: SeatCounterVisibility;
  /** Set when the seat count isn't a number the visitor can act on — see
   *  `EventAvailability.hideSeatCount`. Suppresses both the visible label and
   *  the seat fragment in the accessible label. */
  hideSeatCount?: boolean;
  /** See `EventAvailability.bookingMaxBelowSeatCount`. Suppresses both labels
   *  unless `seatCounterVisibility` is `'always'`. */
  bookingMaxBelowSeatCount?: boolean;
  /** Optional event-level description rendered below the location row in the
   *  single-session card. Ignored by the multi-session card. */
  description?: string | null;
  /** Cut the description off after two lines. For compact surfaces (the
   *  experience page's booking panel) where a long host text would push the
   *  booking button away; the full text stays in the accessible label. */
  clampDescription?: boolean;
  /** Suppress the inline "Not enough spots — try reducing tickets" notice when
   *  a global reservation rule is also being violated (e.g. min not met).
   *  Reducing tickets in that case would make things worse, so the dialog-
   *  level reservation message is the authoritative instruction. */
  hideCapacityNotice?: boolean;
};

export type { EventCardProps };

// Where the language badges go, by how many there are: one fits beside the
// date; two or more get a row of their own above it, so the date keeps the
// full width and the time and place stay together.
function placeLanguages(count: number): 'title' | 'top' | null {
  if (count === 0) return null;
  if (count === 1) return 'title';
  return 'top';
}

// A touch more air between the badge row and the date than the stack's own
// gap, so the badges read as a header rather than part of the time block.
const topLanguagesStyle = css({
  marginBottom: 'tiny',
});

const dateTitleStyle = css({
  '&::first-letter': {
    textTransform: 'capitalize',
  },
});

const clampedDescriptionStyle = css({
  lineClamp: '2',
});

const statusLabelStyle = css({
  flexShrink: 0,
  whiteSpace: 'nowrap',
});

// Hover feedback for the multi-session card. Applied conditionally in TSX
// (rather than in the recipe) because the frame is a <div> — `:disabled`
// can't gate it in CSS the way it does on the single variant's <button>.
const multiRootHoverStyles = css({
  _hover: {
    borderColor: 'border.active',
  },
});

// One line, like the single card's location, so a long address can't push
// the "Session details" toggle down. The full list sits behind the toggle.
const multiLocationStyle = css({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

/** The places an event's sessions are held, each once, in session order. */
function distinctLocations(sessions: EventCardSession[]) {
  const byId = new Map<string, NonNullable<EventCardSession['location']>>();
  for (const { location } of sessions) {
    if (location && !byId.has(location.id)) byId.set(location.id, location);
  }
  return [...byId.values()];
}

/**
 * Shared by both cards so their accessible labels can't drift apart. `null`
 * when the seat count isn't a number the visitor can act on — reading out
 * "10 available spots" is as misleading to a screen reader as showing it.
 */
function formatSeatCountFragment(
  intl: ReturnType<typeof useIntl>,
  availableSeats: number,
  suppressed: boolean
): string | null {
  if (suppressed) return null;
  return intl.formatMessage(
    {
      defaultMessage: '{count} available spots',
      description:
        'Seat-count fragment appended to a time slot accessible label',
    },
    { count: availableSeats }
  );
}

export function EventCard(props: EventCardProps) {
  if (props.sessions.length > 1) {
    return <MultiSessionEventCard {...props} />;
  }
  return <SingleSessionEventCard {...props} />;
}

function SingleSessionEventCard({
  sessions,
  description,
  clampDescription = false,
  locales,
  status,
  availableSeats,
  selected,
  onClick,
  seatCounterVisibility = 'limitedOnly',
  hideSeatCount = false,
  bookingMaxBelowSeatCount = false,
  hideCapacityNotice = false,
}: EventCardProps) {
  const intl = useIntl();
  const session = sessions[0]!;
  const { startTime, endTime, location } = session;
  const isSoldOut = status === 'soldOut';
  const isUnavailable = status === 'unavailable';
  const isSelected = selected && !isSoldOut;

  // The shared formatTime pins UTC (naive event times are venue wall-clock,
  // like the rest of the app) — plain `intl.formatTime` here used the runtime
  // timezone, which hydration-mismatched between the UTC server and the
  // visitor's browser (US-10915).
  const formattedStart = formatTime(intl.locale, parseTime(startTime));

  const formattedEnd = endTime
    ? formatTime(intl.locale, parseTime(endTime))
    : null;

  const timeRange = formattedEnd
    ? `${formattedStart} – ${formattedEnd}`
    : formattedStart;

  const seatCountFragment = isSoldOut
    ? null
    : formatSeatCountFragment(
        intl,
        availableSeats,
        isSeatCountSuppressed(
          hideSeatCount,
          bookingMaxBelowSeatCount,
          seatCounterVisibility
        )
      );

  let ariaLabel = timeRange;
  if (location) ariaLabel += `, ${formatLocation(location, 'full')}`;
  // The button's label replaces the flag avatars' hidden names, so the
  // language has to be spelled out here or a screen reader never hears it.
  if (locales.length > 0)
    ariaLabel += `, ${formatLanguageNames(intl, locales)}`;
  if (description) ariaLabel += `, ${description}`;
  if (seatCountFragment) ariaLabel += `, ${seatCountFragment}`;
  if (isSoldOut) {
    ariaLabel = intl.formatMessage(
      {
        defaultMessage: '{details}, sold out',
        description: 'Accessible label for a sold out time slot',
      },
      { details: ariaLabel }
    );
  } else if (isUnavailable) {
    ariaLabel = intl.formatMessage(
      {
        defaultMessage: '{details}, not enough for your selection',
        description:
          'Accessible label for a time slot that cannot accommodate the ticket selection',
      },
      { details: ariaLabel }
    );
  }

  const languagePlacement = placeLanguages(locales.length);
  const styles = eventCard({
    variant: 'single',
    status,
    selected: isSelected,
  });

  const showUnavailableNotice =
    isUnavailable && isSelected && !hideCapacityNotice;

  return (
    <div>
      <button
        type="button"
        // `isUnavailable` means the slot can't fit the current ticket selection
        // — block the click so the visitor can't freshly pick it. (A slot that
        // *was* selected and later became unavailable due to ticket changes
        // keeps its visual `selected` state and renders `showUnavailableNotice`
        // below; that path isn't blocked here because the prop `selected`
        // drives it, not a fresh click.)
        disabled={isSoldOut || isUnavailable}
        aria-pressed={isSelected}
        aria-label={ariaLabel}
        onClick={onClick}
        className={styles.root}
        data-testid="event-card"
      >
        <Stack gap="xsmall">
          <Stack gap="micro">
            {languagePlacement === 'top' && (
              <EventLanguages
                locales={locales}
                muted={isSoldOut}
                layout="row"
                className={cx(styles.secondaryText, topLanguagesStyle)}
              />
            )}

            {/* Single `<EventTime>` render-prop returns the title + details
                JSX so we only invoke `useIntl()` and `deriveEventTimeKind`
                once per card. Render-prop preserves the recipe's tighter
                `body-small-regular` title / `caption` details pairing. */}
            <EventTime sessions={[{ startTime, endTime }]}>
              {({ title, details }) => (
                <>
                  <Stack
                    direction="row"
                    align="center"
                    justify="space-between"
                    gap="small"
                  >
                    <Text
                      style="body-small-regular"
                      className={cx(styles.timeText, dateTitleStyle)}
                      suppressHydrationWarning
                    >
                      {title}
                    </Text>
                    {languagePlacement === 'title' && (
                      <EventLanguages
                        locales={locales}
                        muted={isSoldOut}
                        className={styles.secondaryText}
                      />
                    )}
                  </Stack>
                  {details && (
                    <Text
                      style="caption"
                      aria-hidden="true"
                      className={styles.secondaryText}
                      suppressHydrationWarning
                    >
                      {details}
                    </Text>
                  )}
                </>
              )}
            </EventTime>

            <Stack
              direction="row"
              gap="small"
              align="start"
              justify="space-between"
            >
              {location && (
                <Text
                  style="caption"
                  aria-hidden="true"
                  className={cx(
                    styles.secondaryText,
                    css({
                      maxWidth: '3/5',
                      minWidth: '[0]',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    })
                  )}
                >
                  <FormattedLocation location={location} />
                </Text>
              )}

              <AvailabilityLabel
                status={status}
                availableSeats={availableSeats}
                seatCounterVisibility={seatCounterVisibility}
                hideSeatCount={hideSeatCount}
                bookingMaxBelowSeatCount={bookingMaxBelowSeatCount}
                className={statusLabelStyle}
              />
            </Stack>
          </Stack>

          {description && (
            <Text
              style="caption"
              aria-hidden="true"
              className={cx(
                styles.secondaryText,
                clampDescription && clampedDescriptionStyle
              )}
            >
              {description}
            </Text>
          )}
        </Stack>
      </button>
      {showUnavailableNotice && <UnavailableNotice />}
    </div>
  );
}

function MultiSessionEventCard({
  sessions,
  locales,
  status,
  availableSeats,
  selected,
  onClick,
  seatCounterVisibility = 'limitedOnly',
  hideSeatCount = false,
  bookingMaxBelowSeatCount = false,
  hideCapacityNotice = false,
}: EventCardProps) {
  const intl = useIntl();
  const isSoldOut = status === 'soldOut';
  const isUnavailable = status === 'unavailable';
  const isSelected = selected && !isSoldOut;
  const showUnavailableNotice =
    isUnavailable && isSelected && !hideCapacityNotice;

  const firstSession = sessions[0]!;
  const sessionCount = sessions.length;
  const showLocale = locales.length >= 1;
  const languagePlacement = placeLanguages(locales.length);

  const styles = eventCard({
    variant: 'multi',
    status,
    selected: isSelected,
  });

  const startsDate = intl.formatDate(parseTime(firstSession.startTime), {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });

  // Re-derive the kind so the aria-label's span fragment matches whatever
  // `<EventTime>` picks for the visible subtitle below.
  const kind = deriveEventTimeKind(sessions);
  const sessionsLabel =
    kind === 'long-multi'
      ? intl.formatMessage(
          {
            defaultMessage:
              '{count, plural, one {# session} other {# sessions}} starting {date} over {span, plural, one {# week} other {# weeks}}',
            description:
              'Accessible label for a multi-session time slot spanning more than a week',
          },
          {
            count: sessionCount,
            date: startsDate,
            span: weekSpan(sessions),
          }
        )
      : kind === 'short-multi'
        ? intl.formatMessage(
            {
              defaultMessage:
                '{count, plural, one {# session} other {# sessions}} starting {date} over {span, plural, one {# day} other {# days}}',
              description:
                'Accessible label for a multi-session time slot spanning a week or less',
            },
            {
              count: sessionCount,
              date: startsDate,
              span: daySpan(sessions),
            }
          )
        : intl.formatMessage(
            {
              defaultMessage:
                '{count, plural, one {# session} other {# sessions}} starting {date}',
              description:
                'Accessible label for a single-day multi-session time slot select button',
            },
            {
              count: sessionCount,
              date: startsDate,
            }
          );

  const seatCountFragment = isSoldOut
    ? null
    : formatSeatCountFragment(
        intl,
        availableSeats,
        isSeatCountSuppressed(
          hideSeatCount,
          bookingMaxBelowSeatCount,
          seatCounterVisibility
        )
      );
  // Guests pick between dates by where they are held (e.g. one course in Oslo,
  // the next in Trondheim), so the place has to be readable without opening
  // "Session details". Naming one session's place when they differ would send
  // guests to the wrong one, so then we only say how many there are.
  const sessionLocations = distinctLocations(sessions);
  const locationLabel =
    sessionLocations.length === 1
      ? formatLocation(sessionLocations[0]!, 'full')
      : sessionLocations.length > 1
        ? intl.formatMessage(
            {
              defaultMessage:
                '{count, plural, one {# location} other {# locations}}',
              description:
                'Shown on a multi-session time slot whose sessions are held at different places; each place is listed under Session details',
            },
            { count: sessionLocations.length }
          )
        : null;

  let ariaSelectLabel = sessionsLabel;
  if (locationLabel) ariaSelectLabel += `, ${locationLabel}`;
  if (showLocale) ariaSelectLabel += `, ${formatLanguageNames(intl, locales)}`;
  if (seatCountFragment) ariaSelectLabel += `, ${seatCountFragment}`;

  return (
    // The frame is a non-interactive container; the inner select button is
    // the real interactive surface. Splitting the two avoids the axe
    // `nested-interactive` violation we'd hit if a `<button>` (Session
    // details) sat inside another button (the card itself). The select
    // button still covers the whole frame via a stretched `::after` overlay
    // (see the recipe's multi variant), so clicking anywhere on the card —
    // including the expanded session rows — selects the slot; only the
    // "Session details" toggle sits above the overlay. An unstyled
    // outer wrapper holds the card + the inline `UnavailableNotice` as
    // siblings, matching the single variant's structure so the notice
    // renders under the card rather than inside it.
    <div data-testid="event-card">
      <div
        className={cx(
          styles.root,
          !isSoldOut && !isUnavailable && multiRootHoverStyles
        )}
      >
        <Stack gap="xsmall">
          <button
            type="button"
            // See single-variant comment: block fresh clicks on slots that
            // can't fit the current ticket selection.
            disabled={isSoldOut || isUnavailable}
            aria-pressed={isSelected}
            aria-label={ariaSelectLabel}
            onClick={onClick}
            className={styles.selectArea}
          >
            <Stack gap="micro">
              {languagePlacement === 'top' && (
                <EventLanguages
                  locales={locales}
                  muted={isSoldOut}
                  layout="row"
                  className={cx(styles.subtitle, topLanguagesStyle)}
                />
              )}

              <Stack
                direction="row"
                align="center"
                justify="space-between"
                gap="small"
              >
                <EventTime sessions={sessions}>
                  {({ title }) => (
                    <Text
                      style="body-small-regular"
                      className={styles.timeText}
                      suppressHydrationWarning
                    >
                      {title}
                    </Text>
                  )}
                </EventTime>
                {languagePlacement === 'title' && (
                  <EventLanguages
                    locales={locales}
                    muted={isSoldOut}
                    className={styles.subtitle}
                  />
                )}
              </Stack>

              <EventTime sessions={sessions}>
                {({ details }) => (
                  <Text
                    style="caption"
                    className={styles.subtitle}
                    suppressHydrationWarning
                  >
                    {details}
                  </Text>
                )}
              </EventTime>

              {locationLabel && (
                <Text
                  style="caption"
                  aria-hidden="true"
                  className={cx(styles.subtitle, multiLocationStyle)}
                >
                  {locationLabel}
                </Text>
              )}
            </Stack>
          </button>

          <SessionDetailsDisclosure
            sessions={sessions.map((s) => ({
              startTime: s.startTime,
              endTime: s.endTime,
              location: s.location,
            }))}
            disabled={isSoldOut}
            trailing={
              <AvailabilityLabel
                status={status}
                availableSeats={availableSeats}
                seatCounterVisibility={seatCounterVisibility}
                hideSeatCount={hideSeatCount}
                bookingMaxBelowSeatCount={bookingMaxBelowSeatCount}
                className={statusLabelStyle}
              />
            }
          />
        </Stack>
      </div>
      {showUnavailableNotice && <UnavailableNotice />}
    </div>
  );
}

/** Inline "selection doesn't fit" notice shown beneath a selected slot whose
 *  capacity has been exceeded by the current ticket selection. */
function UnavailableNotice() {
  return (
    <Text
      style="caption"
      role="alert"
      className={css({
        display: 'block',
        color: 'status.negative.bold',
        paddingTop: 'xsmall',
        marginInline: 'tiny',
      })}
    >
      <FormattedMessage
        defaultMessage="Not enough spots for your ticket selection. Try reducing the number of tickets."
        description="Error attached to a time slot when the ticket selection exceeds available capacity"
      />
    </Text>
  );
}
